import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { money, formatDateAT } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, AlertTriangle, ArrowRight, LineChart as LineIcon } from "lucide-react";

const T = {
  tr: {
    title: "Nakit Akış · 30 Gün",
    starting: "Bugün", ending: "30 gün sonra",
    minBalance: "En düşük",
    critical: "kritik gün", criticalPl: "kritik gün",
    healthGreen: "Sağlıklı", healthYellow: "Dikkat", healthRed: "Kritik",
    open: "Detayları aç",
    subtitle: "Deterministik hesaplama · Öngörülen bakiye",
  },
  de: {
    title: "Cashflow · 30 Tage",
    starting: "Heute", ending: "In 30 Tagen",
    minBalance: "Tiefstand",
    critical: "kritischer Tag", criticalPl: "kritische Tage",
    healthGreen: "Gesund", healthYellow: "Achtung", healthRed: "Kritisch",
    open: "Details öffnen",
    subtitle: "Deterministische Berechnung · Prognostizierter Saldo",
  },
};

function healthTone(h) {
  if (h === "green") return { bg: "bg-emerald-50", ring: "border-emerald-200", dot: "bg-emerald-500", text: "text-emerald-800" };
  if (h === "yellow") return { bg: "bg-amber-50", ring: "border-amber-200", dot: "bg-amber-500", text: "text-amber-800" };
  return { bg: "bg-rose-50", ring: "border-rose-200", dot: "bg-rose-500", text: "text-rose-900" };
}

export default function ForecastWidget() {
  const { lang } = useI18n();
  const t = T[lang] || T.tr;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/forecast/headline?horizon_days=30");
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <Skeleton className="h-40 w-full" data-testid="forecast-widget-loading" />;
  if (!data) return null;

  const tone = healthTone(data.health);
  const criticals = (data.critical_days || []).length;
  const Icon = data.ending_balance >= data.starting_balance ? TrendingUp : TrendingDown;

  return (
    <Card className={`${tone.ring} border`} data-testid="forecast-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <LineIcon className="h-4 w-4 text-sky-600" />
            {t.title}
          </CardTitle>
          <Badge className={`${tone.bg} ${tone.text} border ${tone.ring}`} data-testid={`forecast-widget-health-${data.health}`}>
            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${tone.dot}`} />
            {data.health === "green" ? t.healthGreen : data.health === "yellow" ? t.healthYellow : t.healthRed}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{t.starting}</div>
            <div className="text-lg font-semibold" data-testid="forecast-widget-starting">{money(data.starting_balance)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <Icon className="h-3 w-3" /> {t.ending}
            </div>
            <div className={`text-lg font-semibold ${data.ending_balance < 0 ? "text-rose-700" : ""}`} data-testid="forecast-widget-ending">
              {money(data.ending_balance)}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{t.minBalance}</div>
            <div className={`text-lg font-semibold ${data.min_balance < 0 ? "text-rose-700" : ""}`} data-testid="forecast-widget-min">
              {money(data.min_balance)}
            </div>
            <div className="text-[10px] text-muted-foreground">{formatDateAT(data.min_balance_date)}</div>
          </div>
        </div>

        {criticals > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs" data-testid="forecast-widget-critical">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
            <span className="text-rose-800 font-medium">
              {criticals} {criticals === 1 ? t.critical : t.criticalPl}
            </span>
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">{t.subtitle}</div>
          <Button asChild size="sm" variant="ghost" data-testid="forecast-widget-open">
            <Link to="/forecast">{t.open} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
